import _ from 'lodash';

import { generateUUID } from 'app/shared/uuid.service';
import { EventRegistry } from 'app/shared/events/event_registry';
import ImsObjectCollection from 'app/shared/collections/ims_object_collection.service';
import UtilityService from 'app/shared/utilities/utility.service';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { IMS_TYPE__ASSET } from '../collections/asset';

const STORE_AND_FORWARD_JOB_STORE_LS_KEY = 'store_and_forward_job_store';

export enum JobType {
    ADD = 'add',
    UPDATE = 'update',
    REMOVE = 'remove',
}

export const HINT_ADD = 'add';
const HINT_STARTED_JOB = 'started_job';
export const HINT_JOB_SUCCESS = 'job_success';
export const HINT_JOB_FAIL = 'job_fail';
const HINT_JOB_CANCELLED = 'job_cancel';
const HINT_REF_UPDATE = 'ref_update';

interface StoreAndForwardJobRunInfo {
    failCount: number;
    badRequestFailCount: number;
    lastFailReason: string;
    lastStatusCode: number;
}

export interface StoreAndForwardJob {
    uuid?: string; // No need to set this if creating a job
    jobType: JobType;
    objResource: any; // result of $resource.toJSON()
    oldObjResource?: any; // result of $resource.toJSON(). Only needed for update job type
    running?: boolean; // for cloud-sync indicator. No need to set this if creating a job
    // For housing keeping and potentially job squashing
    imsItemType: string;
    // Run info exists after
    runInfo?: StoreAndForwardJobRunInfo;
}

type StoreAndForwardChangeEventCallback = (ev: StoreAndForwardChangeEvent) => void;

export interface StoreAndForwardChangeEvent {
    jobCount: number;
    hint: string;
    job: StoreAndForwardJob;
    eventData?: any;
}

export class StoreAndForwardJobStore {
    private jobQueue: StoreAndForwardJob[];
    private jobStoreChangedEvent: EventRegistry;
    private rmCollectionListeners: any;
    private imsItemTypeRegistry: ImsItemTypeRegistry;
    private localStorageService: Storage;

    constructor(imsItemTypeRegistry: ImsItemTypeRegistry, localStorageService: Storage) {
        if (!imsItemTypeRegistry) {
            throw new Error('imsItemTypeRegistry must be truthy');
        }
        this.localStorageService = localStorageService;
        this.imsItemTypeRegistry = imsItemTypeRegistry;
        this.jobQueue =
            JSON.parse(this.localStorageService.getItem(STORE_AND_FORWARD_JOB_STORE_LS_KEY)) || [];
        this.rmCollectionListeners = [];
        this.jobStoreChangedEvent = new EventRegistry();
    }

    /**
     * Returns a mutable job. Please don't change it or things will break
     *
     * @param {StoreAndForwardJob} job
     * @returns {StoreAndForwardJob}
     */
    public addJob(job: StoreAndForwardJob): StoreAndForwardJob {
        if (job.uuid) {
            throw new Error('dont set job.uuid when adding a job');
        }
        job.uuid = generateUUID();
        job.running = false;
        job.runInfo = {
            failCount: 0,
            badRequestFailCount: 0,
            lastFailReason: null,
            lastStatusCode: null,
        };
        this.jobQueue.push(job);
        this.saveStateToLocalStorage();
        this.emitChangedEvent(HINT_ADD, job);
        return job;
    }

    public cancelJob(job: StoreAndForwardJob): void {
        const matchingJobs = this.jobQueue.filter((j) => j.uuid === job.uuid);
        if (matchingJobs.length > 1) {
            throw new Error('Cant have more than 1 match??');
        }
        if (matchingJobs.length === 1) {
            const job = matchingJobs[0];
            if (job.running) {
                throw new Error('Cant cancel a job while its running');
            }
            if (job.jobType === JobType.ADD && job.objResource.id < 0) {
                // It is an add job using a temporary negative ID.
                // TODO: Will need to cascade the delets onto other new objects
                // that use this temporary ID.
                const collection: ImsObjectCollection = this.getCollectionForType(job.imsItemType);
                const tempObj = collection.createImsObject(job.objResource);
                collection.removeObject(tempObj);
            }
            // Remove the job!
            this.jobQueue = this.jobQueue.filter((j) => j.uuid !== job.uuid);
            this.saveStateToLocalStorage();
            this.emitChangedEvent(HINT_JOB_CANCELLED, job);
        }
        // If no match do nothing for now.
        // There might be a small race where a user
        // attempts to cancel the job at the same time the job completes.
    }

    public async getJobPromise(job: StoreAndForwardJob): Promise<any> {
        const rmListener = null;
        try {
            return await new Promise((resolve, reject) => {
                this.addOnChangeListener((ev: StoreAndForwardChangeEvent) => {
                    // Some job store events don't provide a job. Skip it in this case!
                    if (ev.job) {
                        if (job.uuid === ev.job.uuid) {
                            const hint = ev.hint;
                            if (hint === HINT_JOB_SUCCESS) {
                                resolve(ev);
                            } else if (hint === HINT_JOB_FAIL) {
                                reject(ev);
                            }
                        }
                    }
                });
            });
        } finally {
            if (rmListener) {
                rmListener();
            }
        }
    }

    /**
     * Returns the next job to run. Shallow clone only, DONT EDIT IT.
     * Avoiding the deep clone in case the contained resource objects are large.
     *
     * Only a single job can be run at a time.
     *
     * Returning null means there are no jobs to run.
     */
    public getJobToRun(): StoreAndForwardJob {
        if (this.jobQueue.length) {
            if (this.jobQueue[0].running) {
                throw new Error('There is already one job running, only one job can run at a time');
            } else {
                this.jobQueue[0].running = true;
                this.emitChangedEvent(HINT_STARTED_JOB, this.jobQueue[0]);
                return _.clone(this.jobQueue[0]);
            }
        } else {
            return null;
        }
    }

    /**
     * Tells the job store that the currently running job has succeeded
     * The job is removed and change events are fired.
     * @param {StoreAndForwardJob} job
     * @param crudResult The result of calling resource.$save or any other operation.
     * It will be a resource object or perhaps an array of resource objects in the future.
     * Use job.jobType to determine what to expect.
     */
    public jobSuccess(job: StoreAndForwardJob, crudResult: any) {
        this.runningJobSanityCheck(job);
        // Job is successful, remove it from the queue and make it disappear for good
        this.jobQueue.shift();
        this.saveStateToLocalStorage();
        this.emitChangedEvent(HINT_JOB_SUCCESS, job, crudResult);
    }

    /**
     * Tells the job store that the currently running job has failed
     * @param {StoreAndForwardJob} job
     * @param error A JS error object
     */
    public jobFailed(job: StoreAndForwardJob, error) {
        this.runningJobSanityCheck(job);
        const head = this.jobQueue[0];
        head.running = false;
        ++head.runInfo.failCount;
        head.runInfo.lastStatusCode = error.status ? error.status : null;
        const reason = UtilityService.apiErrorIntoText(
            error,
            this.getCollectionForType(job.imsItemType),
        );
        head.runInfo.lastFailReason = reason ? reason : null;

        if (head.runInfo.lastStatusCode === 400) {
            ++head.runInfo.badRequestFailCount;
            if (head.runInfo.badRequestFailCount >= 3) {
                console.log('removing job because it has http400-ed 3 or more times', head);
                // Job has failed, remove it from the queue and make it disappear for good
                this.jobQueue.shift();
                this.saveStateToLocalStorage();
            }
        }

        this.emitChangedEvent(HINT_JOB_FAIL, job, error);
    }

    /**
     * Returns a copy of the job queue so clients cant edit it
     * directly
     */
    public peekJobQueue() {
        return this.jobQueue.slice();
    }

    public getJobCount() {
        return this.jobQueue.length;
    }

    public isJobRunning() {
        return this.getJobCount() > 0 && this.jobQueue[0].running;
    }

    /**
     * Could just add collections to everything inside the ims item registry ?
     * If we do this...need to be careful to connect all of the listeners after the
     * registry is completely populated.
     * @param {ImsObjectCollection} collection
     */
    public addCollectionToWatch(collection: ImsObjectCollection) {
        this.rmCollectionListeners.push(
            collection.addLocalToPersistentObjListener(this.idChange.bind(this)),
        );
    }

    public queryJobs(jobTypes: JobType[], imsItemTypes: string[], resourceMatcher: any): any[] {
        return this.jobQueue.filter((job) => {
            return (
                jobTypes.includes(job.jobType) &&
                imsItemTypes.includes(job.imsItemType) &&
                _.isMatch(job.objResource, resourceMatcher)
            );
        });
    }

    public queryResources(
        jobTypes: JobType[],
        imsItemTypes: string[],
        resourceMatcher: any,
    ): any[] {
        return this.queryJobs(jobTypes, imsItemTypes, resourceMatcher).map(
            (job) => job.objResource,
        );
    }

    public hasAddJobInQueue(imsItemType: string, imsId: number): boolean {
        if (!imsItemType) {
            throw new Error('imsItemType must be truthy');
        }
        if (!imsId) {
            throw new Error('imsId must be truthy');
        }
        const result = this.queryResources([JobType.ADD], [imsItemType], {
            id: imsId,
        });
        if (result.length > 1) {
            throw new Error(`Error, double add job for the same id: ${imsItemType} ${imsId}`);
        }
        return result.length === 1;
    }

    /**
     * In normal use this is hooked to the
     * ImsObjectCollection.addLocalToPersistentObjListener event and by the
     * StoreAndForwarder when a duplicate ID is detected
     *
     * This is a deep value search and replace matching on ${imsType}_id as the key.
     *
     * @param {number} oldId
     * @param {number} newId
     * @param {string} imsType
     */
    public idChange(oldId: number, newId: number, imsType: string) {
        const standardIdKey = `${imsType}_id`;
        // Mutate the queue. We'll probably need to expand on the standard id later with
        // a special lookup depending on the imsType of the job
        this.jobQueue.forEach((job) => {
            replaceIds(job, [standardIdKey], oldId, newId);
            replaceJobObjResourcePk(job, imsType, oldId, newId);
        });
        this.saveStateToLocalStorage();
        this.emitChangedEvent(HINT_REF_UPDATE, null);
    }

    /**
     * For our own sanity...
     * @param {StoreAndForwardJob} job
     */
    private runningJobSanityCheck(job: StoreAndForwardJob) {
        if (!this.jobQueue.length) {
            throw new Error('We think a job is running but there are no jobs!!');
        }
        if (!this.jobQueue[0].running) {
            throw new Error('There is already one job running, only one job can run at a time');
        }
        if (Object.is(job, this.jobQueue[0])) {
            // Would normally make this immutable. Trying a different approach where we
            // do a sanity check for equality. We humbly ask the programmer to take care
            // when modifying the contents of the job queue.
            throw new Error("Job instances do not match. Don't clone these objects");
        }
        if (job.uuid !== this.jobQueue[0].uuid) {
            throw new Error('mismatch job uuids');
        }
    }

    /**
     * Some listeners use the hint to do targeted handling
     * @param {string} hint
     * @param job
     * @param eventData
     */
    private emitChangedEvent(hint: string, job: StoreAndForwardJob, eventData?: any) {
        this.jobStoreChangedEvent.notifyListeners({
            jobCount: this.getJobCount(),
            hint: hint,
            job: job,
            eventData: eventData,
        });
    }

    public addOnChangeListener(callback: StoreAndForwardChangeEventCallback): () => void {
        return this.jobStoreChangedEvent.addEventListener(callback);
    }

    private getCollectionForType(imsItemType: string) {
        const typeDetails = this.imsItemTypeRegistry.getTypeDetails(imsItemType);
        if (!typeDetails) {
            throw new Error(`typeDetails is null for ${imsItemType}`);
        }
        const collection = typeDetails.collection;
        if (!collection) {
            throw new Error(`collection is null for ${imsItemType}`);
        }
        return collection;
    }

    private saveStateToLocalStorage(): void {
        this.localStorageService.setItem(
            STORE_AND_FORWARD_JOB_STORE_LS_KEY,
            JSON.stringify(this.jobQueue),
        );
    }
}

function replaceIds(thing: any, keys: string[], oldId: number, newId: number) {
    if (_.isObject(thing)) {
        return Object.assign(
            thing,
            Object.keys(thing).reduce((acc, k) => {
                if (keys.includes(k)) {
                    if (_.isArray(thing[k])) {
                        acc[k] = thing[k].map((value) => (value === oldId ? newId : value));
                    } else if (thing[k] === oldId) {
                        acc[k] = newId;
                    } else {
                        // Not sure if this will cause a problem or not. Log an error...
                        console.error(
                            'Unhandled, may cause problems: thing, k, acc',
                            thing,
                            k,
                            acc,
                        );
                    }
                } else {
                    acc[k] = replaceIds(thing[k], keys, oldId, newId);
                }
                return acc;
            }, {}),
        );
    } else if (_.isArray(thing)) {
        return thing.map((innerObj) => replaceIds(thing, keys, oldId, newId));
    } else {
        return thing;
    }
}

function replaceJobObjResourcePk(
    job: StoreAndForwardJob,
    imsItemType: string,
    oldId: number,
    newId: number,
): void {
    if (job.imsItemType === imsItemType && job.objResource.id === oldId) {
        job.objResource.id = newId;
        if (job.oldObjResource) {
            job.oldObjResource.id = newId;
        }
        if (job.imsItemType === IMS_TYPE__ASSET && job.jobType === JobType.ADD) {
            // This code path is used when we discover that our object with a unique constraint,
            // asset.name, already exists on the server. We can't add it again, so instead
            // we patch it! Keeping the behaviour very narrow on purpose
            job.jobType = JobType.UPDATE;
        }
    }
}
